<template>
  <div class="modal fade" :class="{ show: modelValue }" :style="{ display: modelValue ? 'block' : 'none' }"
    tabindex="-1" @click.self="closeModal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title">
            <i class="fas fa-palette me-2"></i>
            Test Display Settings
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <div class="alert alert-info mb-4">
            <i class="fas fa-info-circle me-2"></i>
            These settings will be applied to all conditional tests using this template.
          </div>

          <!-- Margins & Padding Section -->
          <div class="settings-section mb-4">
            <h6 class="section-title">
              <i class="fas fa-expand-arrows-alt me-2"></i>
              Margins & Padding
            </h6>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Container Margin (px)</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="localSettings.containerMargin"
                  min="0"
                  max="100"
                  placeholder="20"
                />
                <small class="text-muted">Outer spacing around the test container</small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Container Padding (px)</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="localSettings.containerPadding"
                  min="0"
                  max="100"
                  placeholder="20"
                />
                <small class="text-muted">Inner spacing inside the test container</small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Question Spacing (px)</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="localSettings.questionSpacing"
                  min="0"
                  max="50"
                  placeholder="15"
                />
                <small class="text-muted">Space between questions</small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Page Padding (px)</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="localSettings.pagePadding"
                  min="0"
                  max="100"
                  placeholder="15"
                />
                <small class="text-muted">Padding inside each page</small>
              </div>
            </div>
          </div>

          <!-- Background Color Section -->
          <div class="settings-section mb-4">
            <h6 class="section-title">
              <i class="fas fa-fill-drip me-2"></i>
              Background Colors
            </h6>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Container Background</label>
                <div class="color-input-group">
                  <input
                    type="color"
                    class="form-control form-control-color"
                    v-model="localSettings.containerBackground"
                  />
                  <input
                    type="text"
                    class="form-control"
                    v-model="localSettings.containerBackground"
                    placeholder="#ffffff"
                  />
                </div>
                <small class="text-muted">Main container background color</small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Page Background</label>
                <div class="color-input-group">
                  <input
                    type="color"
                    class="form-control form-control-color"
                    v-model="localSettings.pageBackground"
                  />
                  <input
                    type="text"
                    class="form-control"
                    v-model="localSettings.pageBackground"
                    placeholder="#f8f9fa"
                  />
                </div>
                <small class="text-muted">Individual page background color</small>
              </div>
            </div>
          </div>

          <!-- Max Width Section -->
          <div class="settings-section mb-4">
            <h6 class="section-title">
              <i class="fas fa-arrows-alt-h me-2"></i>
              Container Width
            </h6>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Max Width (px)</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="localSettings.maxWidth"
                  min="600"
                  max="2000"
                  step="50"
                  placeholder="1200"
                />
                <small class="text-muted">Maximum width of the test container (0 = full width)</small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Container Alignment</label>
                <select class="form-select" v-model="localSettings.containerAlignment">
                  <option value="center">Center</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
                <small class="text-muted">Horizontal alignment of the container</small>
              </div>
            </div>
          </div>

          <!-- Text & Button Section -->
          <div class="settings-section mb-4">
            <h6 class="section-title">
              <i class="fas fa-font me-2"></i>
              Text & Button Styling
            </h6>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Text Color</label>
                <div class="color-input-group">
                  <input
                    type="color"
                    class="form-control form-control-color"
                    v-model="localSettings.textColor"
                  />
                  <input
                    type="text"
                    class="form-control"
                    v-model="localSettings.textColor"
                    placeholder="#000000"
                  />
                </div>
                <small class="text-muted">Color for question text and content</small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Submit Button Position</label>
                <select class="form-select" v-model="localSettings.submitButtonAlignment">
                  <option value="center">Center</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
                <small class="text-muted">Horizontal alignment of submit button</small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Submit Button Top Margin (px)</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="localSettings.submitButtonMarginTop"
                  min="0"
                  max="100"
                  placeholder="20"
                />
                <small class="text-muted">Space above the submit button</small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Submit Button Bottom Margin (px)</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="localSettings.submitButtonMarginBottom"
                  min="0"
                  max="100"
                  placeholder="20"
                />
                <small class="text-muted">Space below the submit button</small>
              </div>
            </div>
          </div>

          <!-- Preview Section -->
          <div class="settings-section">
            <h6 class="section-title">
              <i class="fas fa-eye me-2"></i>
              Preview
            </h6>
            <div class="preview-container" :style="previewStyle">
              <div class="preview-page" :style="previewPageStyle">
                <div class="preview-question" :style="previewQuestionStyle">
                  <strong :style="{ color: localSettings.textColor }">Question 1</strong>
                  <p class="mb-0" :style="{ color: localSettings.textColor }">This is a preview of how your test will look</p>
                </div>
                <div class="preview-question" :style="previewQuestionStyle">
                  <strong :style="{ color: localSettings.textColor }">Question 2</strong>
                  <p class="mb-0" :style="{ color: localSettings.textColor }">With the current display settings</p>
                </div>
                <div :style="previewButtonStyle">
                  <button class="btn btn-primary" disabled>Submit Answer</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="resetToDefaults">
            <i class="fas fa-undo me-1"></i>
            Reset to Defaults
          </button>
          <button type="button" class="btn btn-outline-secondary" @click="closeModal">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="saveSettings">
            <i class="fas fa-save me-1"></i>
            Save Settings
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade" :class="{ show: modelValue }" v-if="modelValue"></div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  currentSettings: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['update:modelValue', 'save']);

const defaultSettings = {
  containerMargin: 20,
  containerPadding: 20,
  questionSpacing: 15,
  pagePadding: 15,
  containerBackground: '#ffffff',
  pageBackground: '#f8f9fa',
  maxWidth: 1200,
  containerAlignment: 'center',
  textColor: '#000000',
  submitButtonAlignment: 'center',
  submitButtonMarginTop: 20,
  submitButtonMarginBottom: 20
};

const localSettings = ref({ ...defaultSettings, ...props.currentSettings });

watch(() => props.currentSettings, (newSettings) => {
  localSettings.value = { ...defaultSettings, ...newSettings };
}, { deep: true });

const previewStyle = computed(() => ({
  margin: `${localSettings.value.containerMargin}px auto`,
  padding: `${localSettings.value.containerPadding}px`,
  backgroundColor: localSettings.value.containerBackground,
  maxWidth: localSettings.value.maxWidth > 0 ? `${localSettings.value.maxWidth}px` : '100%',
  marginLeft: localSettings.value.containerAlignment === 'left' ? '0' :
              localSettings.value.containerAlignment === 'right' ? 'auto' : 'auto',
  marginRight: localSettings.value.containerAlignment === 'right' ? '0' :
               localSettings.value.containerAlignment === 'left' ? 'auto' : 'auto'
}));

const previewPageStyle = computed(() => ({
  padding: `${localSettings.value.pagePadding}px`,
  backgroundColor: localSettings.value.pageBackground,
  borderRadius: '8px',
  border: '1px solid #dee2e6'
}));

const previewQuestionStyle = computed(() => ({
  marginBottom: `${localSettings.value.questionSpacing}px`,
  padding: '10px',
  backgroundColor: 'white',
  borderRadius: '4px',
  border: '1px solid #e0e0e0'
}));

const previewButtonStyle = computed(() => ({
  marginTop: `${localSettings.value.submitButtonMarginTop}px`,
  marginBottom: `${localSettings.value.submitButtonMarginBottom}px`,
  textAlign: localSettings.value.submitButtonAlignment
}));

const closeModal = () => {
  emit('update:modelValue', false);
};

const saveSettings = () => {
  emit('save', { ...localSettings.value });
  closeModal();
};

const resetToDefaults = () => {
  if (confirm('Are you sure you want to reset all settings to default values?')) {
    localSettings.value = { ...defaultSettings };
  }
};
</script>

<style scoped>
.modal.show {
  display: block;
  animation: fadeIn 0.15s ease-in;
}

.modal-backdrop.show {
  opacity: 0.5;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.settings-section {
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f8f9fa;
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #dee2e6;
}

.color-input-group {
  display: flex;
  gap: 10px;
}

.form-control-color {
  width: 60px;
  height: 38px;
  padding: 2px;
}

.preview-container {
  border: 2px dashed #dee2e6;
  min-height: 200px;
  border-radius: 8px;
}

.preview-question:last-child {
  margin-bottom: 0 !important;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}
</style>
